import { Inject, Injectable } from '@angular/core';

import { uniqBy, cloneDeep } from 'lodash';
import { MessageService } from 'primeng/api';
import * as uuid from 'uuid';
import { catchError, combineLatest, map, mergeMap, Observable, of, take, last, tap, takeLast, throwError } from 'rxjs';

import { AbstractPageBuilderService } from '@Common/PageBuilder/services/abstract-page-builder.service';
import { Mention } from '@Common/Mentions/models/mention';
import { messageSeverityEnum } from '@Common/Misc/enums/message-severity.enum';
import { CoreDialogService } from '@Core/Dialogs/services/dialog.service';
import { AIApi } from '@api/ai/AI-api';
import { SearchCriteriaSortFieldEnum } from '@api/mentions/enum/search-criteria-sort-field.enum';
import { SortOrderEnum } from '@api/shared/enums/sort-order.enum';
import { LocalizationService } from '@Core/Localization/services/localization.service';
import { MentionSearchService } from '@Monitor/Core/services/mention-search.service';
import { MonitoringSeriesTypeEnum } from '@Monitor/Shared/enums/monitoring-series-type.enum';
import { MonitoringBackEndDataSourceModel } from '@Monitor/Shared/models/monitoring-back-end-data-source.model';
import { GeneratingAnalysisDialogComponent } from '../components/GeneratingAnalysisDialog/generating-analysis-dialog.component';
import { CoverageInsights } from '../../Shared/models/coverage-insights.model';
import { BriefingsService } from './briefings.service';
import { DynamicMentionListDisplayElementModel } from '../models/dynamic-mention-list-display-element.model';
import { CoverageAnalysisService, pickRandomItems } from '../../Shared/services/coverage-analysis.service';
import { AnalysisDisplayElementModel } from '../models/briefing-analysis-display-element.model';
import { BlockModel } from '../../../Common/PageBuilder/models/block.model';
import { CuratorBlock } from '../../../Common/PageBuilder/models/curator-block.model';
import { ConfirmDialogComponent } from '../../../Common/Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { ProgressDialogComponent } from '../../../Common/Dialogs/components/ProgressDialog/progress-dialog.component';
import { ProgressModel } from '../../../Common/Dialogs/models/progress.model';
import { AiAnalysisTypes } from '../../../Common/Mentions/enums/ai-analysis-types.enum';
import { sortArray } from '../../../Common/Misc/helpers/sort/sort.helper';
import { BriefingBlockTypeEnum } from '../enums/briefing-block-type.enum';
import { BriefingTitleDisplayElementModel } from '../models/briefing-title-display-element.model';
import { MentionSearchModel } from '../../Shared/models/mention-search.model';
import { SavedSearchType } from '../../../Common/MonitorShared/enum/saved-search-type.enum';
import { CuratedMentionListDisplayElementModel } from '../models/curated-mention-list-display-element.model';
import { briefingBlockTypeEnumToBlockTypeMap } from '../models/briefings-block-types.model';
import { BriefingStyleStateEnum } from '../../../Common/MonitorShared/enum/briefing-style-state.enum';
import { MentionsService } from '../../Core/services/mentions.service';
import { CoverageInsightsApi } from '../../../api/ai/model/coverage-insights-api.model';
import { MentionModel } from '../../../api/mentions/model/mention.model';
import { KeyMessageGroupApi } from '../../../api/ai/model/key-message-group-api.';
import { BriefingModel } from '../models/briefing.model';


import { MentionSearchCriteriaModel } from '../../../Monitor/Shared/models/mention-search-criteria.model';

@Injectable()
export class BriefingCoverageAnalysisService extends CoverageAnalysisService {

    readonly DEFAULT_PREVIEW_MENTIONS: number = 5;

    allCuratedMentions: string[] = [];
    mentionsOnBriefing: Mention[];
    totalMentions: number;
    currentBriefing: BriefingModel;
    inprogressGenerations: string[] = [];
    analysisBlockWithAllCuratedCoverage: BlockModel[];

    constructor(
        public aiApi: AIApi,
        public mentionSearchService: MentionSearchService,
        public coreDialogService: CoreDialogService,
        public messageService: MessageService,
        public localizationService: LocalizationService,
        private mentionsService: MentionsService,
        @Inject('BriefingsService') public briefingsService: AbstractPageBuilderService,
    ) {
        super(aiApi, mentionSearchService, coreDialogService, messageService, localizationService);
        this.subscriptions.push(
            (this.briefingsService as BriefingsService).getCurrentBriefingMentions().subscribe(
                mentions => {
                    mentions = uniqBy(
                        mentions.filter(
                            m => m.canGenerateInsight
                        ),
                        m => m.mentionId ?? m.id
                    );

                    this.mentionsOnBriefing = mentions;
                    this.totalMentions = mentions?.length;
                    this.allCuratedMentions = mentions.map(mention => {
                        if (mention.mentionId) {
                            return mention.mentionId;
                        } else {
                            return mention.id;
                        }
                    });

                    if (this.analysisBlockWithAllCuratedCoverage?.length && this.inprogressGenerations.length === 0) {
                        this.generateAiContentForBlocks(this.analysisBlockWithAllCuratedCoverage);
                        this.analysisBlockWithAllCuratedCoverage = [];
                    }
                }
            ),
            (this.briefingsService as BriefingsService).getFocusedResource().subscribe(x => this.currentBriefing = x)
        );
    }

    generateAnalysis(coverageInsights: CoverageInsights, dataSource: MonitoringBackEndDataSourceModel) {
        const coverageInsightsApi = this.prepareAnalysisPayload(coverageInsights, dataSource);

        if (coverageInsightsApi.searchRequest || coverageInsightsApi.mentionIds?.length) {
            const analysisSubscription = this.aiApi.analysis(coverageInsightsApi).subscribe({
                next: (response) => {
                    if (response?.html) {
                        // CK editor does not deal with <br/> well
                        const html = response.html?.replace(/<br\/>/g, '<p>&nbsp;</p>')
                            .replace(/<p>/g, '<p style="line-height: 150%;">')
                            .replace(/\u0002/g, '\'')
                            .replace(/<li>/g, '<li style="line-height: 150%;">');
                        this.generatedContentSubject.next(html);
                    }
                    this.coreDialogService.close('GeneratingAnalysisDialog');
                },
                error: (err) => {
                    this.errorMessage(err);
                }
            });

            const io = {
                outputs: {
                    dialogClose: (cancelled: boolean) => {
                        if (cancelled) {
                            analysisSubscription?.unsubscribe();
                        }
                    }
                }
            }
            this.coreDialogService.open(GeneratingAnalysisDialogComponent, io);
        }
    }

    isValidInsightPayload(coverageInsightsApi: CoverageInsightsApi): boolean {
        return (coverageInsightsApi?.mentionIds?.length > 0) || !!coverageInsightsApi?.searchRequest;
    }

    public generateAiContentForBlocks(
        blocks: BlockModel[],
        progress?: ProgressModel
    ): void {
        blocks?.forEach(block => {
            if (!block?.displayElement) {
                return;
            }
            const displayElement = block.displayElement as AnalysisDisplayElementModel;
            displayElement.loading = true;
            const coverageInsightsApi = this.prepareAnalysisPayload(
                displayElement.analysisField,
                displayElement.backendDataSource,
                (block.displayElement as AnalysisDisplayElementModel).initialSourceMentionSearchModel
            );
            if (!this.isValidInsightPayload(coverageInsightsApi)) {
                displayElement.loading = false;
                return;
            }
            this.aiApi.analysis(coverageInsightsApi).subscribe({
                next: (response) => {
                    if (response?.html) {
                        // CK editor does not deal with <br/> well
                        const html = response.html?.replace(/<br\/>/g, '<p>&nbsp;</p>')
                            .replace(/<p>/g, '<p style="line-height: 150%;">')
                            .replace(/\u0002/g, '\'')
                            .replace(/<li>/g, '<li style="line-height: 150%;">');
                        displayElement.text = html;
                        displayElement.loading = false;
                        if (progress) {
                            progress.currentIndex++;
                            this.checkProgressDialog(progress);
                        }
                        this.briefingsService.notifyBlockDisplayElementChanged(block);
                    }
                },
                error: (err) => {
                    displayElement.text = '';
                    displayElement.loading = false;
                    this.errorMessage(err);
                    if (progress) {
                        progress.currentIndex++;
                        this.checkProgressDialog(progress);
                    }
                    this.briefingsService.notifyBlockDisplayElementChanged(block);
                }
            });
        });
    }

    private prepareAnalysisPayload(
        coverageInsights: CoverageInsights,
        dataSource: MonitoringBackEndDataSourceModel,
        initialLoadMentionSearchModel?: MentionSearchModel
    ): CoverageInsightsApi {
        const coverageInsightsApi = coverageInsights.toApiModel();
        this.mentionSearchService.clearSearch();
        this.mentionSearchService.setSortField(SearchCriteriaSortFieldEnum.published);
        this.mentionSearchService.setSortOrder(SortOrderEnum.descending);

        if (dataSource.type === MonitoringSeriesTypeEnum.allCuratedCoverageInBrief) {
            coverageInsightsApi.mentionIds = pickRandomItems(cloneDeep(this.allCuratedMentions), 400);
        } else if (dataSource.type === MonitoringSeriesTypeEnum.coverageInSection) {
            if (dataSource.referenceId) {
                if ((this.briefingsService as BriefingsService).getMentionIdsOfCuratedBlock(dataSource.referenceId)) {
                    coverageInsightsApi.mentionIds = (this.briefingsService as BriefingsService).getMentionIdsOfCuratedBlock(dataSource.referenceId);
                } else {
                    const referenceBlock = (this.briefingsService as BriefingsService).getReferenceBlock(dataSource.referenceId);
                    this.mentionSearchService.setSortField(dataSource.sourceSearchCriteria.sortField);
                    this.mentionSearchService.setSortOrder(dataSource.sourceSearchCriteria.sortOrder);
                    this.mentionSearchService.setSearchCriteria(dataSource.sourceSearchCriteria);
                    this.mentionSearchService.setSampleFilter(
                        { basedOnMentionAmount: (referenceBlock.displayElement as DynamicMentionListDisplayElementModel).pageSize }
                    );
                    this.mentionSearchService.populateFiltersFromSearchQuery(dataSource.filterSearchCriteria?.query);
                    const searchCriteria = this.mentionSearchService.getMentionSearchCriteriaPayload(dataSource.filterSearchCriteria);
                    if (searchCriteria) {
                        coverageInsightsApi.isAccountSearch = searchCriteria.isAccountSearch;
                        if (dataSource.filterSearchCriteria?.showSimilar != null) {
                            searchCriteria.showSimilar = dataSource.filterSearchCriteria.showSimilar;
                        }
                        coverageInsightsApi.searchRequest = searchCriteria.getApiEntity();
                    }
                }
            }
        } else {
            const searchCriteria = this.getSearchRequest(dataSource, initialLoadMentionSearchModel);
            if (searchCriteria) {
                coverageInsightsApi.isAccountSearch = searchCriteria.isAccountSearch;
                coverageInsightsApi.searchRequest = searchCriteria.getApiEntity();
            }
        }

        return coverageInsightsApi;
    }

    getSearchRequest(dataSource: MonitoringBackEndDataSourceModel, initialLoadMentionSearchModel?: MentionSearchModel): MentionSearchCriteriaModel {
        const blankMentionSearchCriteria = new MentionSearchCriteriaModel();
        blankMentionSearchCriteria.query.keywordGroups = [];

        if (dataSource) {
            // HACK for refresh data => we only set dataSource.sourceSearchCriteria when we click on the block,
            // in the MonitoringSourcePickerFieldComponent.. so if we have 10 blocks, and initial load the page..
            // all 10 blocks will be "blank" searchCriteria when we click "refresh data" but on load, we set displayElement.filterMentionSearchModel..
            // which is the default search criteria source, so we can use that if sourceSearchCriteria is not set
            if (initialLoadMentionSearchModel &&
                !initialLoadMentionSearchModel.searchCriteria?.equals(blankMentionSearchCriteria, true, true, true, true, true) &&
                dataSource.sourceSearchCriteria?.equals(blankMentionSearchCriteria, true, true, true, true, true)
            ) {
                this.mentionSearchService.setSearchCriteria(initialLoadMentionSearchModel.searchCriteria);
            } else {
                this.mentionSearchService.setSearchCriteria(dataSource.sourceSearchCriteria);
            }
            this.mentionSearchService.populateFiltersFromSearchQuery(dataSource.filterSearchCriteria?.query);
        }

        this.mentionSearchService.setSampleFilter(
            { sampleAmount: 200 }
        );

        const searchCriteria = this.mentionSearchService.getMentionSearchCriteriaPayload(dataSource.filterSearchCriteria);
        if (searchCriteria && dataSource.filterSearchCriteria?.showSimilar != null) {
            searchCriteria.showSimilar = dataSource.filterSearchCriteria.showSimilar;
        }
        return searchCriteria;
    }

    private errorMessage(err) {
        this.coreDialogService.close('GeneratingAnalysisDialog');

        if (err?.error?.details?.[0]?.invalidValue?.includes('the number of mentions that allow ai process is 0')) {
            this.messageService.add({
                severity: messageSeverityEnum.error,
                summary: 'Briefings.analysis.noCoverage.error.label',
                detail: 'Briefings.analysis.noCoverage.error.message',
            });
        } else {
            this.messageService.add({
                severity: messageSeverityEnum.error,
                summary: 'Briefings.analysis.analyzingMentions',
                detail: 'Briefings.analysis.analyzingMentions.error',
            });
        }
    }

    resetCurator(curatorBlock: CuratorBlock): void {
        this.coreDialogService.open(ConfirmDialogComponent, {
            inputs: {
                id: 'briefing_reset_curator_confirm_dialog',
                heading: 'PageBuilder.blockType.curation.reset',
                alertMessage: 'PageBuilder.blockType.curation.reset.message.line1',
                alertType: messageSeverityEnum.warning,
                message: 'PageBuilder.blockType.curation.reset.message.line2',
                okLabel: 'PageBuilder.blockType.curation.reset.ok',
            },
            outputs: {
                dialogClose: (confirmed: boolean) => {
                    if (confirmed) {
                        curatorBlock.blocks = curatorBlock.originalBlocks;
                        curatorBlock.displayElement.hasGeneratedContent = false;
                        curatorBlock.originalBlocks = [];
                        curatorBlock.blocks.forEach(block => {
                            if((block.type.key === BriefingBlockTypeEnum.AUTOMATIC_MENTION_LIST || block.type.key === BriefingBlockTypeEnum.ANALYSIS)
                                && block.displayElement.automaticallyGenerated
                            ) {
                                const dynamicMentionDisplayElement =
                                    block.displayElement as DynamicMentionListDisplayElementModel | AnalysisDisplayElementModel;
                                dynamicMentionDisplayElement.backendDataSource = curatorBlock.displayElement.backendDataSource;
                                dynamicMentionDisplayElement.searchCriteriaId = curatorBlock.displayElement.searchCriteriaId;
                                dynamicMentionDisplayElement.sourceType = curatorBlock.displayElement.sourceType;
                            }
                        });
                        this.briefingsService.formFieldUpdated();
                        this.briefingsService.selectBlock(curatorBlock);
                    }
                }
            }
        });
    }

    curatorAnalyze(
        curatorBlock: CuratorBlock,
        isFullCanvasGeneration: boolean = false
    ): void {
        if (isFullCanvasGeneration) {
            this.curatorGeneration(curatorBlock, true);
        } else {
            this.coreDialogService.open(ConfirmDialogComponent, {
                inputs: {
                    id: 'briefing_curator_analyze_confirm_dialog',
                    heading: 'PageBuilder.blockType.curation.analyzeCoverage',
                    alertMessage: 'PageBuilder.blockType.curation.analyzeCoverage.message.line1',
                    alertType: messageSeverityEnum.warning,
                    message: 'PageBuilder.blockType.curation.analyzeCoverage.message.line2',
                    okLabel: 'PageBuilder.blockType.curation.Analyze',
                },
                outputs: {
                    dialogClose: (confirmed: boolean) => {
                        if (confirmed) {
                            this.curatorGeneration(curatorBlock);
                        }
                    }
                }
            });
        }
    }

    private curatorGeneration(
        curatorBlock: CuratorBlock,
        isFullCanvasGeneration: boolean = false
    ): void {
        if (!this.validateCurator(curatorBlock) || !curatorBlock?.displayElement) {
            return;
        }

        const generationId = uuid.v4();
        this.inprogressGenerations.push(generationId);

        const progress = new ProgressModel();
        progress.currentIndex = 1;
        progress.totalItems = 4;

        curatorBlock.originalBlocks = curatorBlock.blocks;
        curatorBlock.blocks = [];
        curatorBlock.errors = [];

        let generationObservable: Observable<any>;

        this.coreDialogService.open(ProgressDialogComponent, {
            inputs: {
                id: 'briefing_curator_generation_dialog',
                headerLabel: isFullCanvasGeneration ?
                    'PageBuilder.blockType.curation.analysisGeneration.fullCanvas' : 'PageBuilder.blockType.curation.analysisGeneration',
                generatingText: 'PageBuilder.blockType.curation.analysisGeneration.generating',
                alertMessage: isFullCanvasGeneration ?
                    'PageBuilder.blockType.curation.analysisGeneration.fullCanvas.info' : 'PageBuilder.blockType.curation.analysisGeneration.info',
                alertType: messageSeverityEnum.info,
                progress: progress,
                showCount: false,
                showPercentage: true,
            },
            outputs: {
                dialogClose: (confirmed: boolean) => {
                    if (progress.currentIndex < progress.totalItems) {
                        curatorBlock.blocks = curatorBlock.originalBlocks;
                        curatorBlock.displayElement.hasGeneratedContent = false;
                    }
                }
            }
        });

        const existingMentionIds = this.mentionsOnBriefing.map(mention => {
            if (mention.mentionId) {
                return mention.mentionId;
            } else {
                return mention.id;
            }
        });

        if (curatorBlock.displayElement.bulkAnalysisMessage.aiAnalysisType === AiAnalysisTypes.COLLECTION_KEY_MESSAGES_DEFAULT) {
            generationObservable = this.doCollectionKeyMessage(curatorBlock, progress, existingMentionIds);
        } else {
            generationObservable = this.doTopMentions(curatorBlock, progress, existingMentionIds);
        }

        this.handleObservables(
            generationObservable,
            curatorBlock,
            progress,
            isFullCanvasGeneration,
            generationId,
            true
        );
    }

    refreshCuratorGeneration(curatorBlock: CuratorBlock, progress: ProgressModel): void {
        const generationId = uuid.v4();
        this.inprogressGenerations.push(generationId);

        curatorBlock.originalBlocks = curatorBlock.blocks;
        curatorBlock.blocks = [];
        curatorBlock.errors = [];

        let generationObservable: Observable<any>;

        const existingMentionIds = this.mentionsOnBriefing.map(mention => {
            if (mention.mentionId) {
                return mention.mentionId;
            } else {
                return mention.id;
            }
        });

        if (curatorBlock.displayElement.bulkAnalysisMessage.aiAnalysisType === AiAnalysisTypes.COLLECTION_KEY_MESSAGES_DEFAULT) {
            generationObservable = this.doCollectionKeyMessage(curatorBlock, progress, existingMentionIds);
        } else {
            generationObservable = this.doTopMentions(curatorBlock, progress, existingMentionIds);
        }

        this.handleObservables(
            generationObservable,
            curatorBlock,
            progress,
            false,
            generationId,
            false
        );
    }

    handleObservables(
        generationObservable: Observable<any>,
        curatorBlock: CuratorBlock,
        progress: ProgressModel,
        isFullCanvasGeneration: boolean,
        generationId: string,
        forceCloseDialog: boolean
    ) {
        generationObservable?.pipe(
            takeLast(1),
            tap(() => {
                this.inprogressGenerations = this.inprogressGenerations.filter(id => id !== generationId);
                this.finishCuratorGeneration(progress, curatorBlock, isFullCanvasGeneration, forceCloseDialog);
            }),
            catchError(err => {
                if (
                    (err?.message?.includes('no mention')) ||
                    (err?.error?.details?.[0]?.invalidValue?.includes('the number of mentions that allow ai process is 0')) ||
                    (err?.status === 400 && err?.url?.includes('mentionai/bulk/keymessages'))
                ) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'PageBuilder.blockType.curation.analyzeCoverage.noMention.error.heading',
                        detail: 'PageBuilder.blockType.curation.analyzeCoverage.noMention.error.message'
                    });
                } else {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'PageBuilder.blockType.curation.analyzeCoverage.error.heading',
                        detail: 'PageBuilder.blockType.curation.analyzeCoverage.error.message'
                    });
                }

                curatorBlock.blocks = curatorBlock.originalBlocks;
                curatorBlock.originalBlocks = null;
                if (curatorBlock.displayElement) {
                    curatorBlock.displayElement.hasGeneratedContent = false;
                }
                if (forceCloseDialog) {
                    this.briefingsService.selectBlock(curatorBlock);
                }
                this.inprogressGenerations = this.inprogressGenerations.filter(id => id !== generationId);
                if (forceCloseDialog && !this.inprogressGenerations.length) {
                    if (isFullCanvasGeneration) {
                        (this.briefingsService as BriefingsService).setCoverageImage();
                    }
                    // after all generations are done, close the popup
                    this.coreDialogService.close('Progress');
                }

                if (!forceCloseDialog) {
                    let hasAnalysisBlockAutoGen: boolean = false;
                    curatorBlock.blocks.forEach(child => {
                        if (child.type.key === BriefingBlockTypeEnum.ANALYSIS) {
                            if ((child.displayElement as AnalysisDisplayElementModel)?.automaticallyGenerated) {
                                hasAnalysisBlockAutoGen = true;
                            }
                        }
                    });

                    let progressIncrease = 1;
                    if (err?.url?.includes('mentionai/bulk/analysis') || err?.url?.includes('mentionai/bulk/keymessages')) {
                        if (hasAnalysisBlockAutoGen) {
                            progressIncrease = 3;
                        } else {
                            progressIncrease = 2;
                        }
                    }

                    if (hasAnalysisBlockAutoGen && (err?.url?.includes('mentions/batch/fetch') || err?.url?.includes('mentions/batch/mercurymentionss'))) {
                        progressIncrease = 2;
                    }

                    // not sure how much to increment yet
                    progress.currentIndex += progressIncrease;
                    this.checkProgressDialog(progress);
                }
                this.briefingsService.notifyBlockDisplayElementChanged(curatorBlock);

                return of(null);
            })
        ).subscribe();
    }

    doCollectionKeyMessage(
        curatorBlock: CuratorBlock,
        progress: ProgressModel,
        existingMentionIds: string[]
    ): Observable<any> {
        const curatorDisplayElement = curatorBlock.displayElement;
        // Key narratives
        const coverageInsights = curatorBlock.displayElement.bulkAnalysisMessage;
        const aiBlocks = [];
        let keyMessages: KeyMessageGroupApi[] = [];
        coverageInsights.enableDuplicateMentions = this.currentBriefing.enableDuplicateMentions;

        // combinedSearchCriteria only gets set if we select the block, so we are doing the same hack that we do in the getSearchRequest,
        // because on initial load, we don't "set" any values in backendDataSource
        let searchRequest = curatorDisplayElement.backendDataSource.combinedSearchCriteria;
        if (!curatorDisplayElement?.initialSourceMentionSearchModel?.searchCriteria?.equals(new MentionSearchCriteriaModel(), true, true, true, true, true) &&
            searchRequest?.equals(new MentionSearchCriteriaModel(), true, true, true, true, true)
        ) {
            searchRequest = this.getSearchRequest(curatorDisplayElement.backendDataSource, curatorDisplayElement?.initialSourceMentionSearchModel);
        }

        return this.getKeyMessages(
            coverageInsights,
            searchRequest
        ).pipe(
            mergeMap(items => {
                progress.currentIndex++;
                sortArray(items, 'keyMessageProminenceSum', SortOrderEnum.descending);
                keyMessages = items.slice(0, coverageInsights.numberKeyMessages);

                let ids = Array.from(new Set(keyMessages.flatMap(k => k.examples.filter(Boolean))));

                if (!this.currentBriefing.enableDuplicateMentions) {
                    ids = ids?.filter(x => !existingMentionIds.some(id => id === x));
                }

                let mentionRequest;
                if (ids.length) {
                    if (searchRequest.isAccountSearch) {
                        mentionRequest = this.mentionsService.getMentionByIds(ids).pipe(map(res => {
                            const mentions = [];
                            ids.forEach(id => {
                                if (res[id]) {
                                    mentions.push(res[id]);
                                }
                            });

                            return mentions;
                        }));
                    } else {
                        mentionRequest = this.mentionsService.getMentionByMercuryIds(ids, true);
                    }

                    return mentionRequest;
                } else {
                    return throwError(() => new Error('no mention'));
                }
            }),
            mergeMap((mentions: any) => {
                progress.currentIndex++;
                if (mentions?.length) {
                    keyMessages.forEach(keyMessage => {
                        if (!this.currentBriefing.enableDuplicateMentions) {
                            keyMessage.examples = keyMessage.examples?.filter(example => !existingMentionIds.some(id => id === example));
                            existingMentionIds.push(...keyMessage.examples);
                        }

                        if (keyMessage.examples?.length) {
                            const subAiBlocks = [];
                            curatorBlock.originalBlocks.forEach(block => {
                                let clone = block.clone();
                                switch(clone.type.key) {
                                    case BriefingBlockTypeEnum.TITLE:
                                        if (clone.displayElement.automaticallyGenerated) {
                                            (clone.displayElement as BriefingTitleDisplayElementModel).text = keyMessage.keyMessageGroupTitle;
                                        }
                                        break;
                                    case BriefingBlockTypeEnum.IMAGE:
                                        break;
                                    case BriefingBlockTypeEnum.AUTOMATIC_MENTION_LIST:
                                        if (clone.displayElement.automaticallyGenerated) {
                                            const mentionSearch: MentionSearchModel = new MentionSearchModel();
                                            mentionSearch.type = SavedSearchType.BRIEFING;
                                            // need unique names for the DB
                                            mentionSearch.name = `${+new Date()}${Math.random()}`;
                                            mentionSearch.searchCriteria.query.dateQuery = null;

                                            const manualBlockDisplayElement = new CuratedMentionListDisplayElementModel(
                                                this.localizationService.transform('Briefings.mentionsOfInterest'),
                                                keyMessage.examples,
                                                [],
                                                null,
                                                mentionSearch,
                                                this.briefingsService.getDefaultStyle(
                                                    BriefingBlockTypeEnum.CURATED_MENTION_LIST, BriefingStyleStateEnum.BRIEFING_1
                                                ),
                                                uuid.v4(),
                                                (clone.displayElement as DynamicMentionListDisplayElementModel).mentionPreviewCount ?? this.DEFAULT_PREVIEW_MENTIONS
                                            );

                                            manualBlockDisplayElement.title = keyMessage.keyMessageGroupTitle;

                                            keyMessage.examples.forEach(mentionId => {
                                                mentions.forEach(mention => {
                                                    if (searchRequest.isAccountSearch) {
                                                        if (mentionId === mention.id) {
                                                            manualBlockDisplayElement.mentions.push(mention);
                                                        }
                                                    } else {
                                                        if (mentionId === mention.mercuryId) {
                                                            manualBlockDisplayElement.mentions.push(mention);
                                                        }
                                                    }
                                                });
                                            });

                                            clone = new BlockModel(
                                                null,
                                                briefingBlockTypeEnumToBlockTypeMap.get(BriefingBlockTypeEnum.CURATED_MENTION_LIST),
                                                manualBlockDisplayElement
                                            );

                                            clone.type.twig = this.briefingsService.getDefaultTemplateByBlockType(
                                                BriefingBlockTypeEnum.CURATED_MENTION_LIST,
                                                BriefingStyleStateEnum.BRIEFING_1
                                            );

                                            this.briefingsService.applyGeneralStylesToBlock(clone);
                                            clone.displayElement.styles.heading.color = clone.displayElement.styles.heading.color;
                                            manualBlockDisplayElement.styles.showSectionHeading = false;
                                        }
                                        break;
                                    case BriefingBlockTypeEnum.ANALYSIS:
                                        // For analysis block, we should not anonymize the search criteria when cloning
                                        (clone.displayElement as AnalysisDisplayElementModel).filterMentionSearchModel =
                                            (block.displayElement as AnalysisDisplayElementModel).filterMentionSearchModel?.clone(false);
                                        if (clone.displayElement.automaticallyGenerated) {
                                            const aiDisplayElement = clone.displayElement as AnalysisDisplayElementModel;
                                            if (curatorDisplayElement.bulkAnalysisMessage.focus) {
                                                aiDisplayElement.analysisField.focus =
                                                    (curatorDisplayElement.bulkAnalysisMessage.focus + ' / ' + keyMessage.keyMessageGroupTitle)
                                                        .substring(0, 255);
                                            } else {
                                                aiDisplayElement.analysisField.focus = keyMessage.keyMessageGroupTitle.substring(0, 255);
                                            }

                                            aiDisplayElement.generateOnSend = false;

                                            if (!aiDisplayElement.analysisField.aiAnalysisType) {
                                                aiDisplayElement.analysisField.aiAnalysisType = AiAnalysisTypes.EXECUTIVE_SUMMARY;
                                            }

                                            subAiBlocks.push(clone);
                                        }

                                        break;
                                    default:
                                        break;
                                }

                                clone.displayElement.displayType = curatorBlock.displayElement.displayType;
                                curatorBlock.blocks.push(clone);
                            });

                            aiBlocks.push(subAiBlocks);
                        }
                    });
                }

                // generate ai block content
                if (aiBlocks?.length) {
                    const aiCalls: Observable<any>[] = [];
                    aiBlocks.forEach((subAiBlocks, index) => {
                        subAiBlocks.forEach(subAiBlock => {
                            const subAiDisplayElement = subAiBlock.displayElement as AnalysisDisplayElementModel;
                            const coverageInsightsApi = subAiDisplayElement.analysisField.toApiModel();
                            coverageInsightsApi.mentionIds = keyMessages[index]?.examples;
                            coverageInsightsApi.isAccountSearch = searchRequest.isAccountSearch;
                            const aiCall = this.aiApi.analysis(coverageInsightsApi).pipe(
                                tap(response => {
                                    if (response?.html) {
                                        // CK editor does not deal with <br/> well
                                        const html = response.html?.replace(/<br\/>/g, '<p>&nbsp;</p>')
                                            .replace(/<p>/g, '<p style="line-height: 150%;">')
                                            .replace(/\u0002/g, '\'')
                                            .replace(/<li>/g, '<li style="line-height: 150%;">');
                                        subAiDisplayElement.text = html;
                                    }
                                    this.briefingsService.notifyBlockDisplayElementChanged(subAiBlock);
                                }),
                                catchError((err) => {
                                    subAiDisplayElement.text = '';
                                    this.briefingsService.notifyBlockDisplayElementChanged(subAiBlock);
                                    return of(null);
                                })
                            );

                            aiCalls.push(aiCall);
                        });
                    });

                    return aiCalls.length ? combineLatest(aiCalls) : of([]);
                } else {
                    return of([]);
                }
            })
        );
    }

    doTopMentions(
        curatorBlock: CuratorBlock,
        progress: ProgressModel,
        existingMentionIds: string[]
    ): Observable<any> {
        const curatorDisplayElement = curatorBlock.displayElement;
        // Top mentions
        const aiBlocks = [];
        let mentionIds = [];

        const coverageInsightsApi =
            this.prepareAnalysisPayload(
                curatorDisplayElement.bulkAnalysisMessage,
                curatorDisplayElement.backendDataSource,
                curatorDisplayElement.initialSourceMentionSearchModel
            );

        if (!coverageInsightsApi.bulkAnalysisMessage.numberMentions) {
            coverageInsightsApi.bulkAnalysisMessage.numberMentions = 20;
            curatorBlock.originalBlocks.forEach(block => {
                if (block.type.key === BriefingBlockTypeEnum.AUTOMATIC_MENTION_LIST && block.displayElement.automaticallyGenerated) {
                    coverageInsightsApi.bulkAnalysisMessage.numberMentions = (block.displayElement as DynamicMentionListDisplayElementModel).pageSize;
                }
            });
        }

        if (coverageInsightsApi.searchRequest || coverageInsightsApi.mentionIds?.length) {
            return this.aiApi.analysis(coverageInsightsApi).pipe(
                mergeMap(res => {
                    progress.currentIndex++;
                    mentionIds = Array.from(new Set([...mentionIds, ...res.mentionIds.filter(Boolean)]));

                    if (!this.currentBriefing.enableDuplicateMentions) {
                        mentionIds = mentionIds?.filter(x => !existingMentionIds.some(id => id === x));
                    }

                    if (!mentionIds.length) {
                        return throwError(() => new Error('no mention'));
                    }

                    if (curatorDisplayElement.backendDataSource.combinedSearchCriteria.isAccountSearch) {
                        return this.mentionsService.getMentionByIds(mentionIds).pipe(map(x => {
                            const mentions = [];
                            mentionIds.forEach(id => {
                                if (x[id]) {
                                    mentions.push(x[id]);
                                }
                            });

                            return mentions;
                        }));
                    } else {
                        return this.mentionsService.getMentionByMercuryIds(mentionIds, true);
                    }
                }),
                mergeMap((mentions: MentionModel[]) => {
                    progress.currentIndex++;

                    // Cap at 255 chars: this title is persisted to the BriefingCuratedMentions.title
                    // column (VARCHAR(255)). The curator focus can be much longer (e.g. the quarterly
                    // template's "Positive representation of: <Analysis Focus>"), which would otherwise
                    // overflow the column and fail the insert.
                    const title = (curatorDisplayElement.bulkAnalysisMessage.focus
                        ? curatorDisplayElement.bulkAnalysisMessage.focus
                        : curatorBlock.displayElement.bulkAnalysisMessage.aiAnalysisType === AiAnalysisTypes.COLLECTION_CUSTOM
                            ? this.localizationService.transform('PageBuilder.blockType.curation.settings.type.custom.defaultTitle')
                            : this.localizationService.transform('PageBuilder.blockType.curation.settings.type.topMentions'))
                        .substring(0, 255);

                    curatorBlock.originalBlocks.forEach(block => {
                        let clone = block.clone();
                        clone.displayElement.isBoundToGlobalStyles = block.displayElement.isBoundToGlobalStyles;

                        switch(clone.type.key) {
                            case BriefingBlockTypeEnum.TITLE:
                                const titleDisplayElement = clone.displayElement as BriefingTitleDisplayElementModel;
                                if (titleDisplayElement.automaticallyGenerated) {
                                    titleDisplayElement.text = title;
                                }
                                break;
                            case BriefingBlockTypeEnum.IMAGE:
                                // TODO automatically generate image later
                                break;
                            case BriefingBlockTypeEnum.AUTOMATIC_MENTION_LIST:
                                if (clone.displayElement.automaticallyGenerated) {
                                    const automaticBlockDisplayElement = clone.displayElement;
                                    const mentionSearch: MentionSearchModel = new MentionSearchModel();
                                    mentionSearch.type = SavedSearchType.BRIEFING;
                                    // need unique names for the DB
                                    mentionSearch.name = `${+new Date()}${Math.random()}`;
                                    mentionSearch.searchCriteria.query.dateQuery = null;

                                    const manualBlockDisplayElement = new CuratedMentionListDisplayElementModel(
                                        this.localizationService.transform('Briefings.mentionsOfInterest'),
                                        mentionIds,
                                        mentions,
                                        null,
                                        mentionSearch,
                                        this.briefingsService
                                            .getDefaultStyle(BriefingBlockTypeEnum.CURATED_MENTION_LIST, BriefingStyleStateEnum.BRIEFING_1),
                                        uuid.v4(),
                                        // eslint-disable-next-line max-len
                                        (automaticBlockDisplayElement as DynamicMentionListDisplayElementModel).mentionPreviewCount ?? this.DEFAULT_PREVIEW_MENTIONS
                                    );

                                    manualBlockDisplayElement.title = title;

                                    clone = new BlockModel(
                                        null,
                                        briefingBlockTypeEnumToBlockTypeMap.get(BriefingBlockTypeEnum.CURATED_MENTION_LIST),
                                        manualBlockDisplayElement
                                    );

                                    clone.type.twig = this.briefingsService.getDefaultTemplateByBlockType(
                                        BriefingBlockTypeEnum.CURATED_MENTION_LIST,
                                        BriefingStyleStateEnum.BRIEFING_1
                                    );

                                    this.briefingsService.applyGeneralStylesToBlock(clone);
                                    manualBlockDisplayElement.styles.showSectionHeading = false;
                                    manualBlockDisplayElement.styles.heading.color = automaticBlockDisplayElement.styles.heading.color;
                                    manualBlockDisplayElement.isBoundToGlobalStyles = automaticBlockDisplayElement.isBoundToGlobalStyles;
                                }
                                break;
                            case BriefingBlockTypeEnum.ANALYSIS:
                                // For analysis block, we should not anonymize the search criteria when cloning
                                (clone.displayElement as AnalysisDisplayElementModel).filterMentionSearchModel =
                                    (block.displayElement as AnalysisDisplayElementModel).filterMentionSearchModel?.clone(false);
                                if (clone.displayElement.automaticallyGenerated) {
                                    const aiDisplayElement = clone.displayElement as AnalysisDisplayElementModel;
                                    aiDisplayElement.analysisField.focus = curatorDisplayElement.bulkAnalysisMessage.focus;
                                    aiDisplayElement.generateOnSend = false;

                                    aiBlocks.push(clone);
                                }

                                break;
                            default:
                                break;
                        }

                        clone.displayElement.displayType = curatorBlock.displayElement.displayType;
                        curatorBlock.blocks.push(clone);
                    });

                    // generate ai block content
                    if (aiBlocks?.length) {
                        const aiCalls: Observable<any>[] = [];
                        aiBlocks.forEach(aiBlock => {
                            const aiDisplayElement = aiBlock.displayElement as AnalysisDisplayElementModel;
                            const aiBlockCoverageInsightsApi = aiDisplayElement.analysisField.toApiModel();
                            aiBlockCoverageInsightsApi.mentionIds = mentionIds;
                            aiBlockCoverageInsightsApi.isAccountSearch = curatorDisplayElement.backendDataSource.combinedSearchCriteria.isAccountSearch;
                            aiDisplayElement.generateOnSend = false;
                            const aiCall = this.aiApi.analysis(aiBlockCoverageInsightsApi).pipe(tap(response => {
                                if (response?.html) {
                                    // CK editor does not deal with <br/> well
                                    const html = response.html?.replace(/<br\/>/g, '<p>&nbsp;</p>')
                                        .replace(/<p>/g, '<p style="line-height: 150%;">')
                                        .replace(/\u0002/g, '\'')
                                        .replace(/<li>/g, '<li style="line-height: 150%;">');
                                    aiDisplayElement.text = html;
                                }
                                this.briefingsService.notifyBlockDisplayElementChanged(aiBlock);
                            }));

                            aiCalls.push(aiCall);
                        });

                        return combineLatest(aiCalls);
                    } else {
                        return of([]);
                    }
                })
            );
        }
        return of(null);
    }

    private finishCuratorGeneration(
        progress: ProgressModel,
        curatorBlock: CuratorBlock,
        isFullCanvasGeneration: boolean,
        forceCloseDialog: boolean
    ): void {
        curatorBlock.displayElement.hasGeneratedContent = true;
        this.briefingsService.formFieldUpdated();
        (this.briefingsService as BriefingsService).triggerDirtyCheck();
        curatorBlock.blocks?.forEach(block => {
            this.briefingsService.notifyBlockDisplayElementChanged(block);
        });
        progress.currentIndex++;
        if (!this.inprogressGenerations.length) {
            if (isFullCanvasGeneration) {
                (this.briefingsService as BriefingsService).setCoverageImage();
            }
            // after all generations are done, close the popup
            if (forceCloseDialog) {
                this.coreDialogService.close('Progress');
            } else {
                this.checkProgressDialog(progress);
            }
        }
    }

    checkProgressDialog(progress: ProgressModel) {
        if (progress.totalItems <= progress.currentIndex) {
            // HACK: set half second timeout to show the progress bar load before closing
            setTimeout(() => {
                this.coreDialogService.close('Progress');
            }, 500);
        }
    }

    validateCurator(curatorBlock: CuratorBlock): boolean {
        let isValid = true;
        let errorBlock;
        curatorBlock?.blocks?.forEach(block => {
            if (block.type.key === BriefingBlockTypeEnum.ANALYSIS) {
                const analysisField = (block.displayElement as AnalysisDisplayElementModel).analysisField;
                if (analysisField.aiAnalysisType === AiAnalysisTypes.BRAND_IMPACT && !analysisField.brand) {
                    errorBlock = block;
                    isValid = false;
                }
            }
        });

        if (errorBlock) {
            this.briefingsService.selectBlock(errorBlock);
        }

        if (!isValid) {
            this.messageService.add({
                severity: messageSeverityEnum.error,
                summary: 'PageBuilder.blockType.curation.analyzeCoverage.innerBlock.error.heading',
                detail: 'PageBuilder.blockType.curation.analyzeCoverage.innerBlock.error.message'
            });
        }

        return isValid;
    }

    setAnalysisBlockWithAllCuratedCoverage(blocks: BlockModel[]): void {
        this.analysisBlockWithAllCuratedCoverage = blocks;
    }
}
